import { useState, useCallback } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { feedbackService } from '../services/feedbackService';
import type { FeedbackTopic, FeedbackRequest } from '../types/feedback.types';

interface UseFeedbackReturn {
  isSubmitting: boolean;
  submitError: string | null;
  isSuccess: boolean;
  submitFeedback: (conversationId: string, topic: FeedbackTopic, message: string) => Promise<void>;
  reset: () => void;
}

export const useFeedback = (): UseFeedbackReturn => {
  const { getAccessToken, user, appUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const submitFeedback = useCallback(
    async (conversationId: string, topic: FeedbackTopic, message: string) => {
      setIsSubmitting(true);
      setSubmitError(null);
      setIsSuccess(false);
      try {
        const token = await getAccessToken();
        if (!token) throw new Error('Unable to obtain access token.');

        const payload: FeedbackRequest = {
          conversation_id: conversationId,
          user_name: appUser?.name ?? user?.name ?? 'Unknown',
          user_email: appUser?.email ?? user?.email ?? 'Unknown',
          topic,
          message: message.trim(),
        };

        await feedbackService.submitFeedback(token, payload);
        setIsSuccess(true);
      } catch (err) {
        setSubmitError(
          err instanceof Error ? err.message : 'Failed to submit feedback. Please try again.',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [getAccessToken, user, appUser],
  );

  const reset = useCallback(() => {
    setIsSubmitting(false);
    setSubmitError(null);
    setIsSuccess(false);
  }, []);

  return { isSubmitting, submitError, isSuccess, submitFeedback, reset };
};
